import React, { useState, useEffect, Fragment, useMemo } from "react";
import Highlight from "react-highlight";
import Swal from "sweetalert2";
import { Row, Col, Card, Carousel, Tab } from "react-bootstrap";

const normalizeS3Url = (raw) => {
  if (!raw || typeof raw !== "string") return raw;

  try {
    const u = new URL(raw);

    if (u.hostname === "predictions-dev.s3.us-east-2.amazonaws.com") {
      u.hostname = "nye-predictions-dev.s3.amazonaws.com";
    }

    let p = u.pathname || "";
    p = p.replace(/\/images\/CommonImages\//g, "/images/common-images/");
    p = p.replace(/\/images\/commonimages\//g, "/images/common-images/");
    p = p.replace(/\/images\/Common-Images\//g, "/images/common-images/");
    u.pathname = p;

    return u.toString();
  } catch {
    return raw;
  }
};

const loadImage = (url, timeoutMs = 8000) =>
  new Promise((resolve) => {
    if (!url) return resolve(false);

    const img = new Image();
    let done = false;

    const finish = (ok) => {
      if (done) return;
      done = true;
      img.onload = null;
      img.onerror = null;
      resolve(ok);
    };

    const t = setTimeout(() => finish(false), timeoutMs);

    img.onload = () => {
      clearTimeout(t);
      finish(true);
    };

    img.onerror = () => {
      clearTimeout(t);
      finish(false);
    };

    img.src = url;
  });

const PredictionPics = () => {
  const [imageUrls, setImageUrls] = useState([]);
  const [validUrls, setValidUrls] = useState([]);
  const [checking, setChecking] = useState(false);

  const groupName = localStorage.getItem("user_initial_Group");

  useEffect(() => {
    const fetchImageUrls = async () => {
      try {
        if (!groupName) return;

        const encodedGroupName = encodeURIComponent(groupName);
        const response = await fetch(
          `${process.env.REACT_APP_PREDICTIONS_API}/api/group/${encodedGroupName}/images`
        );

        if (response.ok) {
          const data = await response.json();
          setImageUrls(data.imageUrls || []);
        } else {
          console.error(
            "Error fetching image URLs. Server responded with:",
            response.status
          );
          setImageUrls([]);
        }
      } catch (error) {
        console.error("Error fetching image URLs:", error);
        setImageUrls([]);
      }
    };

    fetchImageUrls();
  }, [groupName]);

  const normalizedUrls = useMemo(() => {
    const arr = Array.isArray(imageUrls) ? imageUrls : [];
    return arr.map(normalizeS3Url).filter(Boolean);
  }, [imageUrls]);

  useEffect(() => {
    let cancelled = false;

    const checkAll = async () => {
      setChecking(true);
      try {
        const results = await Promise.all(
          normalizedUrls.map(async (url) => ({ url, ok: await loadImage(url) }))
        );

        if (cancelled) return;

        const okOnly = results.filter((x) => x.ok).map((x) => x.url);
        setValidUrls(okOnly);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    if (normalizedUrls.length) checkAll();
    else {
      setValidUrls([]);
      setChecking(false);
    }

    return () => {
      cancelled = true;
    };
  }, [normalizedUrls]);

  const openPreview = (src) => {
    Swal.fire({
      imageUrl: src,
      imageAlt: "image",
      background: "#fff",
      showConfirmButton: false,
      showCloseButton: true,
      padding: 0,
      width: "auto",
      customClass: { popup: "swal-img-popup" },
    });
  };

  const uploadPics = validUrls;

  return (
    <Fragment>
      {/* <PageTitle motherMenu='Bootstrap' activeMenu='Carousel' /> */}
      <div className="container-fluid">
        <div className="element-area">
          <div className="demo-view">
            <div className="container-fluid pt-0 ps-0 pe-lg-4 pe-0">
              <Row>
                <Col xl={12}>
                  <Tab.Container defaultActiveKey="Preview">
                    <Card name="slides-only" className="dz-card">
                      <Tab.Content>
                        <Tab.Pane eventKey="Preview">
                          <Card.Body className="p-4">
                            {checking && (
                              <div style={{ fontSize: 14, opacity: 0.8 }}>
                                Checking images...
                              </div>
                            )}

                            {!checking && uploadPics.length === 0 ? (
                              <div style={{ fontSize: 14, opacity: 0.8 }}>
                                No images
                              </div>
                            ) : (
                              <Carousel
                                style={{
                                  maxHeight: "200px",
                                  overflow: "hidden",
                                }}
                              >
                                {uploadPics.map((carousel, i) => (
                                  <Carousel.Item key={i}>
                                    <div
                                      style={{
                                        height: "200px",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => openPreview(carousel)}
                                    >
                                      <img
                                        src={carousel}
                                        alt=""
                                        className="d-block"
                                        style={{
                                          maxHeight: "100%",
                                          maxWidth: "100%",
                                          objectFit: "contain",
                                        }}
                                      />
                                    </div>
                                  </Carousel.Item>
                                ))}
                              </Carousel>
                            )}
                          </Card.Body>
                        </Tab.Pane>

                        <Tab.Pane eventKey="Code">
                          <div className="card-body pt-0 p-0 code-area">
                            <pre className="mb-0">
                              <code className="language-html">
                                <Highlight>
                                  {`
                                  <Carousel>
                                    {uploadPics.map((carousel, i) => (
                                      <Carousel.Item key={i}>
                                        <div
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => openPreview(carousel)}
                                        >
                                          <img
                                            src={carousel}
                                            className='d-block w-100'
                                            alt={\`Slide \${i + 1}\`}
                                          />
                                        </div>
                                      </Carousel.Item>
                                    ))}
                                  </Carousel>
                                  `}
                                </Highlight>
                              </code>
                            </pre>
                          </div>
                        </Tab.Pane>
                      </Tab.Content>
                    </Card>
                  </Tab.Container>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PredictionPics;
